import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Phone, Flag, AtSign, Linkedin, Instagram, ShieldAlert, Trophy } from 'lucide-react'
import type { JournalistProfileState } from './useJournalistProfile'

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5 break-words">{value || <span className="text-muted-foreground">Not provided</span>}</div>
    </div>
  )
}

export function ProfileOverviewTab({ state }: { state: JournalistProfileState }) {
  const { profile, crmProfile, editing, form, setForm, orgRankings } = state
  const beats = profile?.beats ?? []

  return (
    <div className="space-y-5">
      {(orgRankings ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-muted-foreground" /> Institution rankings
            </CardTitle>
            <p className="text-[11.5px] text-muted-foreground text-justify mt-0.5">
              Your rank among journalists covering each institution, based on submission volume, reach, and recency.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {orgRankings.map((r: any) => (
              <div key={r.org_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50">
                {r.org_logo_url ? (
                  <img src={r.org_logo_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded bg-primary/10 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.org_name}</p>
                  <p className="text-xs text-muted-foreground">{r.article_count} submission{r.article_count === 1 ? '' : 's'}</p>
                </div>
                <Badge variant="outline" className="text-[11px] shrink-0">
                  #{r.org_rank} of {r.org_journalist_count}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {crmProfile && (
        <Card className="border-success/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-success" />
              <p className="text-sm font-semibold">
                Verified by {crmProfile.organizations?.name ?? 'an organisation'}
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {crmProfile.position && <Field label="Position" value={crmProfile.position} />}
              {crmProfile.verification_id && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Verification ID</div>
                  <div className="text-sm mt-0.5 font-mono">{crmProfile.verification_id}</div>
                </div>
              )}
              {crmProfile.press_card_status && (
                <Field label="Press card" value={String(crmProfile.press_card_status).replace(/_/g, ' ')} />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">About</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <>
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Your full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Headline</Label>
                <Input value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder="Parliamentary correspondent, West Africa" />
              </div>
              <div className="space-y-1.5">
                <Label>Bio</Label>
                <Textarea rows={4} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="A brief description of your journalistic focus or expertise…" />
              </div>
              <div className="space-y-1.5">
                <Label>Beats <span className="text-muted-foreground font-normal">(comma-separated)</span></Label>
                <Input value={form.beats_raw} onChange={e => setForm(f => ({ ...f, beats_raw: e.target.value }))} placeholder="Parliament, Trade, Foreign affairs" />
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-justify leading-relaxed">
                {profile?.bio || <span className="text-muted-foreground">No bio yet. Use “Edit profile” to introduce yourself.</span>}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {beats.length > 0
                  ? beats.map(b => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)
                  : <span className="text-xs text-muted-foreground">No beats listed.</span>}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact</CardTitle>
          <p className="text-[11.5px] text-muted-foreground text-justify mt-0.5">
            Sharing this is a requirement of accreditation. Institutions you're accredited to, or have applied to, can always see this information.
          </p>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+234 800 000 0000" />
              </div>
              <div className="space-y-1.5">
                <Label>Nationality</Label>
                <Input value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))} placeholder="Nigeria" />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                <Field label="Phone" value={profile?.phone} />
              </div>
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-muted-foreground shrink-0" />
                <Field label="Nationality" value={profile?.nationality} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-muted-foreground" />
            Emergency Contact
          </CardTitle>
          <p className="text-[11.5px] text-muted-foreground text-justify mt-0.5">
            Used by institutions in case of an emergency during an assignment. Visible to organisations you're accredited to or have applied to.
          </p>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Next of kin</Label>
                <Input value={form.next_of_kin} onChange={e => setForm(f => ({ ...f, next_of_kin: e.target.value }))} placeholder="Full name" />
              </div>
              <div className="space-y-1.5">
                <Label>Emergency contact</Label>
                <Input value={form.emergency_contact} onChange={e => setForm(f => ({ ...f, emergency_contact: e.target.value }))} placeholder="Phone number" />
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Next of kin" value={profile?.next_of_kin} />
              <Field label="Emergency contact" value={profile?.emergency_contact} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Social presence</CardTitle></CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">X / Twitter</Label>
                <Input value={form.social_x} onChange={e => setForm(f => ({ ...f, social_x: e.target.value }))} placeholder="@handle" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">LinkedIn URL</Label>
                <Input value={form.social_linkedin} onChange={e => setForm(f => ({ ...f, social_linkedin: e.target.value }))} placeholder="linkedin.com/in/…" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Instagram</Label>
                <Input value={form.social_instagram} onChange={e => setForm(f => ({ ...f, social_instagram: e.target.value }))} placeholder="@handle" />
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {[
                { icon: AtSign, value: profile?.social_x },
                { icon: Linkedin, value: profile?.social_linkedin },
                { icon: Instagram, value: profile?.social_instagram },
              ].filter(s => !!s.value).map(({ icon: Icon, value }) => (
                <span key={value} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40">
                  <Icon className="w-3 h-3 text-muted-foreground" /> {value}
                </span>
              ))}
              {!profile?.social_x && !profile?.social_linkedin && !profile?.social_instagram && (
                <span className="text-xs text-muted-foreground">No social handles added.</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
